import { useCallback } from "react"
import { signin } from "#/lib/api/client/signin"
import {
  generateBundleForNewUser,
  getMasterPasswordHash,
  getStretchedKey,
  type NewUserCipherBundle,
} from "#/lib/encryption/crypto"
import { socialSignalAxiosInstance } from "../social-signal-api-client"
import { decodeJWT } from "./jwt-utils"
import type { Session } from "./types"
import {
  useCreateSession,
  useInvalidateSession,
  useSession,
} from "./session-context"
import {
  useStoreStretchedMasterKey,
  usePurgeStoredStretchedMasterKey,
  type StoredMasterKey,
} from "#/lib/encryption/master-key-context"
import { signup } from "../api/client/signup"
import type { Result } from "#/lib/types"

export class AuthError extends Error {
  constructor(message?: string, code?: string) {
    super(message)
    this.name = "AuthError"
    this.code = code
  }

  code?: string
}

// Sign in with username/password
function useSignInWithUsername() {
  const createSession = useCreateSession()
  const storeStretchedMasterKey = useStoreStretchedMasterKey()
  return useCallback(
    async (params: {
      username: string
      password: string
    }): Promise<Result<Session, AuthError>> => {
      const cleanedUsername = params.username.trim().toLowerCase()
      try {
        const masterPasswordHash = await getMasterPasswordHash(
          cleanedUsername,
          params.password,
        )

        const rsp = await signin({
          username: cleanedUsername,
          masterPasswordHash,
          clientID: "web",
        })
        const decodedAccessToken = decodeJWT(rsp.accessToken)
        if (
          decodedAccessToken === null ||
          typeof decodedAccessToken.sub === "undefined"
        ) {
          return {
            ok: false,
            error: new AuthError("Sign in failed", "SIGN_IN_ERROR"),
          }
        }

        socialSignalAxiosInstance.defaults.headers.common.Authorization = `Bearer ${rsp.accessToken}`

        const session: Session = {
          accessToken: rsp.accessToken,
          refreshToken: rsp.refreshToken,
          expiresAt: new Date(rsp.expiresAt),
        }
        // Store session in session context
        createSession(session)

        const storedMasterKey: StoredMasterKey = {
          key: await getStretchedKey(cleanedUsername, params.password),
          expiresAt: new Date(rsp.expiresAt),
        }
        storeStretchedMasterKey(storedMasterKey)

        return { ok: true, value: session }
      } catch (error: unknown) {
        return {
          ok: false,
          error: new AuthError(
            error instanceof Error ? error.message : "Sign in failed",
            "SIGN_IN_ERROR",
          ),
        }
      }
    },
    [createSession, storeStretchedMasterKey],
  )
}

function useSignUpWithUsername() {
  const createSession = useCreateSession()
  const storeStretchedMasterKey = useStoreStretchedMasterKey()

  return useCallback(
    async (params: {
      username: string
      password: string
      email: string | undefined
      discoverabilitySettings: {
        discoverableByEmail: boolean
        discoverableByPhone: boolean
        discoverableByUsername: boolean
      }
    }): Promise<Result<Session, AuthError>> => {
      const cleanedUsername = params.username.trim().toLowerCase()

      let bundle: NewUserCipherBundle
      try {
        bundle = await generateBundleForNewUser(
          cleanedUsername,
          params.password,
        )
      } catch (e) {
        console.error(e)
        return {
          ok: false,
          error: new AuthError("Sign up failed", "SIGN_UP_ERROR"),
        }
      }

      try {
        const id = crypto.randomUUID()

        const rsp = await signup({
          id: id,
          username: cleanedUsername,
          masterPasswordHash: bundle.masterPasswordHash,
          protectedUserSymmetricKey: bundle.protectedUserSymmetricKey,
          protectedProfileSymmetricKey: bundle.protectedProfileSymmetricKey,
          protectedRsaPrivateKey: bundle.protectedRsaPrivateKey,
          rsaPublicKey: bundle.rsaPublicKey,
          clientID: "web",
          email: params.email,
          discoverabilitySettings: {
            discoverableByEmail:
              params.discoverabilitySettings.discoverableByEmail,
            discoverableByPhone:
              params.discoverabilitySettings.discoverableByPhone,
            discoverableByUsername:
              params.discoverabilitySettings.discoverableByUsername,
          },
        })

        const decodedAccessToken = decodeJWT(rsp.accessToken)
        if (
          decodedAccessToken === null ||
          typeof decodedAccessToken.sub === "undefined"
        ) {
          return {
            ok: false,
            error: new AuthError("Sign up failed", "SIGN_UP_ERROR"),
          }
        }

        socialSignalAxiosInstance.defaults.headers.common.Authorization = `Bearer ${rsp.accessToken}`

        const session: Session = {
          accessToken: rsp.accessToken,
          refreshToken: rsp.refreshToken,
          expiresAt: new Date(rsp.expiresAt),
        }

        // Store session in session context
        createSession(session)

        const storedMasterKey: StoredMasterKey = {
          key: await getStretchedKey(cleanedUsername, params.password),
          expiresAt: new Date(rsp.expiresAt),
        }
        storeStretchedMasterKey(storedMasterKey)

        return { ok: true, value: session }
      } catch (error: unknown) {
        return {
          ok: false,
          error: new AuthError(
            error instanceof Error ? error.message : "Sign up failed",
            "SIGN_UP_ERROR",
          ),
        }
      }
    },
    [createSession, storeStretchedMasterKey],
  )
}

function useSignout() {
  const invalidateSession = useInvalidateSession()
  const purgeStoredStretchedMasterKey = usePurgeStoredStretchedMasterKey()

  return useCallback(async () => {
    await invalidateSession()
    purgeStoredStretchedMasterKey()
  }, [invalidateSession, purgeStoredStretchedMasterKey])
}

// Create the auth client object that matches better-auth API
export const authClient = {
  useSignInWithUsername,
  useSignUpWithUsername,
  useSignout,
  useSession,
}

export type AuthClient = typeof authClient
